import { unreachable } from 'module/helpers/unreachable';
import { ClassNameOnlyProps } from 'module/types/common';
import { FieldType } from 'module/types/filter-field/field/field';
import { BooleanFieldFilter } from 'module/types/filter-field/filter/boolean';
import { DateFieldFilter } from 'module/types/filter-field/filter/date';
import { EstimateFieldFilter } from 'module/types/filter-field/filter/estimate';
import { FieldFilter } from 'module/types/filter-field/filter/index';
import { MultiSelectFieldFilter } from 'module/types/filter-field/filter/multi-select';
import { MultiUserFieldFilter } from 'module/types/filter-field/filter/multi-user';
import { NumberFieldFilter } from 'module/types/filter-field/filter/number';
import { SingleSelectFieldFilter } from 'module/types/filter-field/filter/single-select';
import { SingleUserFieldFilter } from 'module/types/filter-field/filter/single-user';
import { TextFieldFilter } from 'module/types/filter-field/filter/text';
import { FC } from 'react';
import styled from 'styled-components';
import { BooleanFilterToText } from './boolean-filter';
import { DateFilterToText } from './date-filter';
import { EstimateFilterToText } from './estimate-filter';
import { MultiSelectFilterToText } from './multi-select-filter';
import { MultiUserFilterToText } from './multi-user-filter';
import { NumberFilterToText } from './number-filter';
import { SingleSelectFilterToText } from './single-select-filter';
import { SingleUserFilterToText } from './single-user-filter';
import { TextFilterToText } from './text-filter';
import { UserOptionsGetter } from '../types';

export const FilterCriterionAsText: FC<{
  criterion: FieldFilter;
  userOptionsGetter?: UserOptionsGetter;
}> = ({ criterion, userOptionsGetter }) => {
  const {
    field: { type },
  } = criterion;

  switch (type) {
    case FieldType.Boolean:
      return <BooleanFilterToText filter={criterion as BooleanFieldFilter} />;
    case FieldType.Number:
      return <NumberFilterToText filter={criterion as NumberFieldFilter} />;
    case FieldType.Text:
    case FieldType.MultiLineText:
    case FieldType.Note:
      return <TextFilterToText filter={criterion as TextFieldFilter} />;
    case FieldType.Date:
      return <DateFilterToText filter={criterion as DateFieldFilter} />;
    case FieldType.SingleSelect:
      return (
        <SingleSelectFilterToText
          filter={criterion as SingleSelectFieldFilter}
        />
      );
    case FieldType.SingleUser:
      return (
        <SingleUserFilterToText
          filter={criterion as SingleUserFieldFilter}
          optionsGetter={userOptionsGetter}
        />
      );
    case FieldType.MultiUser:
      return (
        <MultiUserFilterToText
          filter={criterion as MultiUserFieldFilter}
          optionsGetter={userOptionsGetter}
        />
      );
    case FieldType.MultiSelect:
      return criterion.field.fieldName === 'responsible' ? (
        unreachable(type as never)
      ) : (
        <MultiSelectFilterToText filter={criterion as MultiSelectFieldFilter} />
      );
    case FieldType.Assignee:
      return unreachable(type as never);
    //   return <AssigneeFilterToText filter={criterion as AssigneeFieldFilter} />;

    case FieldType.Estimate:
      return <EstimateFilterToText filter={criterion as EstimateFieldFilter} />;
    default:
      return unreachable(type);
  }
};
const CriteriaList = styled.ul``;

export const FilterCriteriaAsText: FC<
  {
    criteria: FieldFilter[];
    userOptionsGetter: UserOptionsGetter;
  } & ClassNameOnlyProps
> = ({ criteria, userOptionsGetter, className }) => {
  return (
    <CriteriaList className={className}>
      {criteria.map((criterion, idx) => (
        <li key={idx}>
          <FilterCriterionAsText
            criterion={criterion}
            userOptionsGetter={userOptionsGetter}
          />
        </li>
      ))}
    </CriteriaList>
  );
};
